import { Button } from "@/components/ui/button";
import { FormattingToolbar } from "@/components/FormattingToolbar";
import { AIInspirationDialog } from "@/components/AIInspirationDialog";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EditorToolbarProps {
  onContentUpdate: (content: string) => void;
  onImageUpload: (imageUrl: string) => void;
  onVideoUpload?: (videoUrl: string) => void;
}

export const EditorToolbar = ({ onContentUpdate, onImageUpload, onVideoUpload }: EditorToolbarProps) => {
  const [showAIDialog, setShowAIDialog] = useState(false);

  const handleInspirationSelect = (text: string) => {
    const editable = document.querySelector('[contenteditable="true"]');
    if (editable) {
      editable.innerHTML = text;
      onContentUpdate(text);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to upload images');
        return;
      }

      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('post_images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('post_images')
        .getPublicUrl(filePath);

      const { error: dbError } = await supabase
        .from('post_images')
        .insert({
          file_path: filePath,
          user_id: user.id
        });

      if (dbError) {
        throw dbError;
      }

      onImageUpload(publicUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleVideoUpload = async (file: File) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to upload videos');
        return;
      }

      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('post_videos')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('post_videos')
        .getPublicUrl(filePath);

      const { error: dbError } = await supabase
        .from('post_videos')
        .insert({
          file_path: filePath,
          user_id: user.id
        });

      if (dbError) {
        throw dbError;
      }

      if (onVideoUpload) {
        onVideoUpload(publicUrl);
      }
      toast.success('Video uploaded successfully');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video');
    }
  };

  return (
    <>
      <FormattingToolbar
        onBold={() => document.execCommand('bold', false)}
        onItalic={() => document.execCommand('italic', false)}
        onEmojiSelect={(emoji: any) => {
          const editable = document.querySelector('[contenteditable="true"]');
          if (editable) {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              range.deleteContents();
              range.insertNode(document.createTextNode(emoji.native));
              onContentUpdate(editable.innerHTML);
            } else {
              editable.innerHTML += emoji.native;
              onContentUpdate(editable.innerHTML);
            }
          }
        }}
        onAIClick={() => setShowAIDialog(true)}
        onImageUpload={handleImageUpload}
        onVideoUpload={handleVideoUpload}
      />
      <AIInspirationDialog
        open={showAIDialog}
        onOpenChange={setShowAIDialog}
        onInspirationSelect={handleInspirationSelect}
      />
    </>
  );
};